import React, { useState } from 'react';
import { Row, Col, Form, Table, Spinner } from 'react-bootstrap';
import Pagination from 'react-js-pagination';
import { useTranslation, Trans } from 'react-i18next';
import BootstrapTable from 'react-bootstrap-table-next';
import { parseDateBR, parseDate, formatDate } from 'helpers/date';
import { DebounceInput } from 'react-debounce-input';
import DatePicker, { registerLocale } from 'react-datepicker';
import BR from 'date-fns/locale/pt-BR';

registerLocale('pt-BR', BR);

const ListingReportsEvent = ({
  reports,
  typeTasks,
  stateFilters,
  handleFilterChange,
  load
}) => {
  const { t } = useTranslation();

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const handlePageChange = (page) => handleFilterChange({ page });

  function calculateQuotaValue(sponsorships) {
    let fund = 0,
      extra = 0;

    sponsorships.map(({ action }) => {
      if(action) {
        action.all_values.map(({ actual_value }) => {
          fund += actual_value;
        });

        action.extra_materials.map(({ value }) => {
          extra += value;
        });
      }      
    });
    return fund + extra;
  }

  function totalRealValue(expenseDescriptions) {
    return expenseDescriptions.reduce((total, valor) => total + valor.value, 0);
  }

  function formatCurrency(value) {
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });
  }

  function changeDateFilter(date, key) {
    const newDate = !date ? null : formatDate(date);
    handleFilterChange({ [key]: newDate });
  }

  return (
    <>
      <Row>
        <Col md={2}>
          <Form.Group>
            <Form.Label>{t('label:records per page')}:</Form.Label>
            <Form.Control
              as="select"
              value={stateFilters.per_page}
              onChange={(e) =>
                handleFilterChange({
                  per_page: e.target.value,
                  page: 1,
                })
              }
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Categoria:</Form.Label>
            <Form.Control
              as="select"
              value={stateFilters.type_task}
              onChange={(e) => {
                handleFilterChange({ type_task: e.target.value, page: 1 });
              }}
            >
              <option value={''}>{t('label:select')}</option>
              {typeTasks.map((task) => (
                <option key={task.id} value={task.id}>
                  {task.name}
                </option>
              ))}
            </Form.Control>
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>Data Inicial:</Form.Label>
            <DatePicker
              className="form-control"
              dateFormat="dd/MM/yyyy"
              locale="pt-BR"
              isClearable
              onChange={(date) => {
                changeDateFilter(date, 'start_date');
                setStartDate(date);
              }}
              selected={startDate}
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>Data Final:</Form.Label>
            <DatePicker
              className="form-control"
              dateFormat="dd/MM/yyyy"
              locale="pt-BR"
              isClearable
              onChange={(date) => {
                changeDateFilter(date, 'end_date');
                setEndDate(date);
              }}
              selected={endDate}
            />
          </Form.Group>
        </Col>
        <Col md={3} className="ml-auto">
          <Form.Group>
            <Form.Label>{t('label:search')}:</Form.Label>
            <DebounceInput
              element={Form.Control}
              debounceTimeout={500}
              value={stateFilters.search}
              onChange={(e) =>
                handleFilterChange({ search: e.target.value, page: 1 })
              }
            />
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col>
          <Table bordered hover responsive id="event-report-table">
            <thead>
              <tr>
                <th className="text-center align-middle" scope="col">
                  {t('label:name')}
                </th>
                <th className="text-center align-middle" scope="col">
                  {t('label:event type')}
                </th>
                <th className="text-center align-middle" scope="col">
                  {t('label:start date')}
                </th>
                <th className="text-center align-middle" scope="col">
                  {t('label:end date')}
                </th>
                <th className="text-center align-middle" scope="col">
                  {t('label:budgeted amount')}
                </th>
                <th className="text-center align-middle" scope="col">
                  {t('label:real value')}
                </th>
                <th className="text-center align-middle" scope="col">
                  {t('label:amount raised')}
                </th>
                <th className="text-center align-middle" scope="col">
                  {t('label:margin')}
                </th>
              </tr>
            </thead>
            <tbody>
              {load?
                <tr><td colSpan={8} className="text-center">
                  <Spinner animation="border" role="status">
                    <span className="sr-only">Loading...</span>
                  </Spinner>
                </td></tr>
                :reports.data.map((rep) => (
                <tr key={rep.id}>
                  <td className="text-center align-middle">{rep.name}</td>
                  <td className="text-center align-middle">{rep.task.name}</td>
                  <td className="text-center align-middle">
                    {parseDateBR(rep.start_date)}
                  </td>
                  <td className="text-center align-middle">
                    {parseDateBR(rep.end_date)}
                  </td>
                  <td className="text-center align-middle">
                    {formatCurrency(rep.budgeted_amount)}
                  </td>
                  <td className="text-center align-middle">
                    {formatCurrency(
                      totalRealValue(rep.event_expense_descriptions)
                    )}
                  </td>
                  <td className="text-center align-middle">
                    {formatCurrency(calculateQuotaValue(rep.sponsorships))}
                  </td>
                  <td className="text-center align-middle">
                    {formatCurrency(
                      calculateQuotaValue(rep.sponsorships) -
                        totalRealValue(rep.event_expense_descriptions)
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Col>
      </Row>
      <Row>
        <Col>
          <Trans i18nKey="label:interpolateShowingRecords">
            Showing {{ from: reports.from || '0' }} to{' '}
            {{ to: reports.to || '0' }} from {{ total: reports.total }} records
          </Trans>
        </Col>
        <Col className="d-flex justify-content-end">
          <Pagination
            activePage={reports.current_page}
            itemsCountPerPage={parseInt(reports.per_page)}
            totalItemsCount={reports.total}
            pageRangeDisplayed={5}
            onChange={handlePageChange}
            itemClass="page-item"
            linkClass="page-link"
            prevPageText="‹"
            nextPageText="›"
          />
        </Col>
      </Row>
    </>
  );
};

export default ListingReportsEvent;
